import later, { ScheduleData } from "@breejs/later";
import { useTranslation } from "@opendash/i18n";
import { Icon } from "@opendash/icons";
import {
  Button,
  Divider,
  Flex,
  Form,
  FormInstance,
  FormListFieldData,
  Tooltip,
} from "antd";
import { observer } from "mobx-react-lite";
import React, { createContext, useState } from "react";
import { CalendarWithoutModeSelect } from "../../../../components/CalendarWithoutModeSelect";
import { parseType_Every } from "../../functions/converters/EveryParser";
import { parseType_OnThe } from "../../functions/converters/OnTheParser";
import { Rule } from "../../types/RuleTypes";
import { LaterRule, RuleContext } from "./LaterRule";

type Props = {
  initialValues?: string;
  onSubmit?: (
    formValues: LaterFormValues,
    humanString: string,
    schedule: ScheduleData
  ) => Promise<void>;
};

export type LaterFormValues = {
  rules: Record<string, Rule>;
};

export const ListFieldContext = createContext<FormListFieldData | undefined>(
  undefined
);

/**
 *
 */
export const LaterForm = observer((props: Props) => {
  const t = useTranslation();
  // const { message } = useFeedback();
  const [form] = Form.useForm<LaterFormValues>();
  const [schedule, setSchedule] = useState<ScheduleData>(undefined);
  const [humanString, setHumanString] = useState<string>("");
  const [lastFormValues, setLastFormValues] = useState<LaterFormValues>();
  const rules = Form.useWatch<FormInstance<LaterFormValues>>("rules", form);

  function handleFinish(values: LaterFormValues) {
    setSchedule(undefined);
    const text = parseLaterFormValues(values);
    const schedule = later.parse.text(text);

    if (schedule.error !== -1) {
      // message.error(t("opendash:cron.later.error"));
      return;
    }

    setSchedule(schedule);
    setHumanString(text);
    setLastFormValues(values);
  }

  function applySchedule() {
    props.onSubmit?.(lastFormValues, humanString, schedule);
  }

  return (
    <Form form={form} onFinish={handleFinish} layout="vertical">
      <Form.List name="rules">
        {(fields, { add, remove }) => (
          <Flex vertical gap="small">
            {fields.map((field) => (
              <ListFieldContext.Provider value={field} key={field.name}>
                <RuleContext.Provider value={rules?.[field.name]}>
                  <LaterRule onRemove={() => remove(field.name)} />
                  <Divider>&</Divider>
                </RuleContext.Provider>
              </ListFieldContext.Provider>
            ))}
            <Button onClick={() => add()}>
              {t("opendash:cron.later.rule.add")}
            </Button>
          </Flex>
        )}
      </Form.List>

      <Flex justify="end" gap="middle">
        <Button htmlType="submit" type="primary">
          {t("opendash:cron.later.check")}
        </Button>

        <Tooltip
          title={!schedule ? t("opendash:cron.later.submit.disabled") : ""}
        >
          <Button
            type="primary"
            disabled={!schedule}
            onClick={() => applySchedule()}
          >
            {t("opendash:cron.later.submit.label")}
          </Button>
        </Tooltip>
      </Flex>

      <Divider
        children={t("opendash:cron.later.preview", {
          humanString: humanString ?? "-",
        })}
      />

      <CalendarWithoutModeSelect
        mode="month"
        disabledDate={(date) =>
          !schedule || !later.schedule(schedule).isValid(date.toDate())
        }
        cellRender={(date) => {
          const isValid =
            schedule && later.schedule(schedule).isValid(date.toDate());

          if (!isValid) return <Icon icon="fa:times" color="red" />;
          return <Icon icon="fa:check" color="green" />;
        }}
      />
    </Form>
  );
});

function parseLaterFormValues(values: LaterFormValues): string {
  let expressions: string[] = [];
  for (const key in values.rules) {
    if (Object.prototype.hasOwnProperty.call(values.rules, key)) {
      const rule = values.rules[key];
      let text = `${rule.type.replace("_", " ")} `;
      switch (rule.type) {
        case "on_the":
          text += parseType_OnThe(rule);
          break;
        case "every":
          text += parseType_Every(rule);
          break;
      }
      expressions.push(text);
    }
  }

  return expressions.join(" and ");
}
